interface UserEmailData {
  email: string;
  lastUpdated: number;
  expirationDate: number;
}

class UserEmailStorageService {
  private readonly STORAGE_KEY = 'aizen_user_email_data';
  private readonly EXPIRATION_DAYS = 20;
  private readonly EXPIRATION_MS = 20 * 24 * 60 * 60 * 1000; // 20 days

  /**
   * Get the stored user email if it exists and hasn't expired
   */
  getUserEmail(): string | null {
    try {
      if (!this.isStorageAvailable()) {
        return null;
      }

      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        return null;
      }

      const data: UserEmailData = JSON.parse(stored);
      
      // Check if expired
      if (this.isExpired(data)) {
        this.clearExpiredData();
        return null;
      }

      return data.email;
    } catch (error) {
      console.error('Error getting user email:', error);
      return null;
    }
  }

  /**
   * Store a user email with expiration
   */
  setUserEmail(email: string): boolean {
    try {
      if (!this.isStorageAvailable()) {
        return false;
      }

      const trimmedEmail = email.trim();
      if (!trimmedEmail) {
        return false;
      }

      const now = Date.now();
      const data: UserEmailData = {
        email: trimmedEmail,
        lastUpdated: now,
        expirationDate: now + this.EXPIRATION_MS
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error('Error setting user email:', error);
      return false;
    }
  }

  /**
   * Check if stored data is expired
   */
  private isExpired(data?: UserEmailData): boolean {
    if (!data) {
      try {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (!stored) return true;
        data = JSON.parse(stored);
      } catch {
        return true;
      }
    }

    return Date.now() > data.expirationDate;
  }

  /**
   * Clear expired data
   */
  clearExpiredData(): void {
    try {
      if (!this.isStorageAvailable()) {
        return;
      }

      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return;

      const data: UserEmailData = JSON.parse(stored);
      if (this.isExpired(data)) {
        localStorage.removeItem(this.STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error clearing expired data:', error);
    }
  }

  /**
   * Clear all stored data
   */
  clearAllData(): void {
    try {
      if (!this.isStorageAvailable()) {
        return;
      }
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing all data:', error);
    }
  }

  /**
   * Check if localStorage is available
   */
  isStorageAvailable(): boolean {
    try {
      const test = 'test';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get days until expiration
   */
  getDaysUntilExpiration(): number {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return 0;

      const data: UserEmailData = JSON.parse(stored);
      const timeLeft = data.expirationDate - Date.now();
      return Math.max(0, Math.ceil(timeLeft / (24 * 60 * 60 * 1000)));
    } catch {
      return 0;
    }
  }
}

// Export singleton instance
export const userEmailStorageService = new UserEmailStorageService(); 